import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useParams} from '@github-ui/react-router'
import {useSearchParams} from '@github-ui/use-navigate'
import {PageHeader, PageLayout, Pagination} from '@primer/react'

import {ArtifactsList} from '../components/ArtifactsList'
import {SidebarNav} from '../components/SidebarNav'
import {artifactRegistryIndexRoute} from './artifact-registry-route'
import type {ArtifactRegistryIndexPayload} from '../types'
import {Blankslate} from '@primer/react/experimental'
import {PackageIcon} from '@primer/octicons-react'
import styles from './ArtifactRegistryIndex.module.css'
import {useCallback} from 'react'

export function ArtifactRegistryIndex() {
  const payload: ArtifactRegistryIndexPayload = useRouteQuery(artifactRegistryIndexRoute, 'mainQuery').data
  const routeParams = useParams<{org: string}>()
  const [searchParams] = useSearchParams()

  const {artifacts, pageInfo, totalCount} = payload
  const org = routeParams.org as string

  // Support both old totalCount and new pageInfo.totalCount
  const artifactsTotalCount = totalCount ?? pageInfo?.totalCount ?? 0

  // Only show pagination if pageInfo exists and has multiple pages
  const shouldShowPagination = pageInfo && pageInfo.totalPages > 1

  const hrefBuilder = useCallback(
    (page: number) => {
      const params = new URLSearchParams(searchParams)
      params.set('page', page.toString())
      return `?${params.toString()}`
    },
    [searchParams],
  )

  const emptyList = (
    <Blankslate>
      <Blankslate.Visual>
        <PackageIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>Track artifacts from build through deployment</Blankslate.Heading>
      <Blankslate.Description className="text-center">
        Link artifacts built on GitHub Actions to external registries and environments with the artifact metadata API
      </Blankslate.Description>
      <Blankslate.SecondaryAction href="https://docs.github.com/en/rest/orgs/artifact-metadata?apiVersion=2022-11-28">
        Learn more about the artifact metadata API
      </Blankslate.SecondaryAction>
    </Blankslate>
  )

  return (
    <PageLayout padding="none" containerWidth="full" columnGap="none">
      <PageLayout.Pane
        position="start"
        divider="line"
        resizable // adding resizable for responsiveness on narrow viewports, otherwise width is constrained
        width={{min: '336px', default: '336px', max: '360px'}}
        className={styles.NavPane}
      >
        <SidebarNav organization={org} selectedTab="linked_artifacts" />
      </PageLayout.Pane>
      <PageLayout.Content as="div" padding="normal">
        {artifacts.length === 0 ? (
          emptyList
        ) : (
          <>
            <PageHeader aria-label="Linked artifacts" as="h3" className="mb-4" hasBorder>
              <PageHeader.TitleArea>
                <PageHeader.Title>Linked artifacts</PageHeader.Title>
              </PageHeader.TitleArea>
            </PageHeader>
            <ArtifactsList org={org} artifacts={artifacts} totalCount={artifactsTotalCount} />
            {shouldShowPagination && (
              <div className="d-flex flex-justify-center mt-4">
                <Pagination
                  pageCount={pageInfo.totalPages}
                  currentPage={pageInfo.currentPage}
                  hrefBuilder={hrefBuilder}
                />
              </div>
            )}
          </>
        )}
      </PageLayout.Content>
    </PageLayout>
  )
}

try{ ArtifactRegistryIndex.displayName ||= 'ArtifactRegistryIndex' } catch {}